// Per-image provenance helpers. Pairs with the backend `data.imageSources`
// array written by functions/propertyIngestionV2/underwriting/imagePipeline.js
// (built by imageCollector.buildImageSources).
//
// Array shape: [{ url, kind, originUrl, platform? }]
//   kind ∈ drive | dropbox | oneDrive | zip | mls | scraped

const normKey = (u) => (typeof u === 'string' ? u.trim() : '');

/**
 * Build a normalized (trimmed-url) lookup from a raw imageSources array.
 * @param {Array<{url, kind, originUrl, platform?}>} imageSources
 * @returns {Map<string, {url, kind, originUrl, platform?}>}
 */
export const buildSourceLookup = (imageSources) => {
    const m = new Map();
    if (Array.isArray(imageSources)) {
        for (const entry of imageSources) {
            const key = normKey(entry?.url);
            if (key && !m.has(key)) m.set(key, entry);
        }
    }
    return m;
};

/**
 * Resolve a displayed image url to its source descriptor.
 * Tries the display url directly (folder images + fallback branch + uploaded
 * enrichment/direct after the post-classifier merge), then falls back to the
 * label's mlsPhotoUrl bridge (firebaseUrl → originalUrl) for the labeled branch.
 * Returns null when unknown — caller renders no badge.
 *
 * @param {Map} lookup     - from buildSourceLookup
 * @param {string} displayUrl
 * @param {Object} [label] - { firebaseUrl, mlsPhotoUrl } if available
 * @returns {{kind, originUrl, platform?}|null}
 */
export const resolveImageSource = (lookup, displayUrl, label) => {
    if (!lookup || lookup.size === 0) return null;
    const direct = lookup.get(normKey(displayUrl));
    if (direct) return direct;
    if (label?.mlsPhotoUrl) {
        const bridged = lookup.get(normKey(label.mlsPhotoUrl));
        if (bridged) return bridged;
    }
    return null;
};
